import type { UserQuestion } from '@elara/domain/models/schemas/widget-schemas';

import React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { useChatInputState, useChatsState } from '#states/hooks.js';
import { Checkbox, RadioGroup, RadioGroupItem, Textarea } from '#ui';
import { useMessageRef } from '#ui/hooks/index.js';

export interface AskUserWidgetProps {
  questions: UserQuestion[];
  interactive?: boolean;
}

type Answer = string | string[] | undefined;

function isSelected(answer: Answer, choice: string): boolean {
  if (!answer) return false;
  const trimmed = choice.trim();
  return Array.isArray(answer) ? answer.includes(trimmed) : answer === trimmed;
}

export function AskUserWidget({ questions, interactive = false }: AskUserWidgetProps) {
  const chatInputState = useChatInputState();
  const chatsState = useChatsState();
  const messageRef = useMessageRef();
  const msgId = messageRef?.messageId;
  const [inputValues, setInputValues] = React.useState<Record<string, string>>({});

  // Refs for input fields and send button for focus management
  const inputRefs = React.useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
  const sendButtonRef = React.useRef<HTMLButtonElement | null>(null);
  // Flag to prevent double-submit when Enter triggers both handleKeyDown and onBlur
  const isSubmittingRef = React.useRef(false);

  const historicalAnswers = chatsState.getAnswersAfterMessage(messageRef?.messageId);

  const updateInput = (question: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [question]: value }));
  };

  const clearInput = (question: string) => {
    setInputValues((prev) => ({ ...prev, [question]: '' }));
  };

  // Save all pending input values before sending
  const saveAllPendingInputs = () => {
    for (const q of questions) {
      const questionText = q.question;
      const typingValue = inputValues[questionText]?.trim();
      const isFreeForm = !q.choices?.length;

      if (typingValue) {
        if (isFreeForm) {
          chatInputState.answerQuestion({ question: questionText, answer: typingValue });
        } else {
          chatInputState.toggleAnswer({ question: questionText, answer: typingValue });
        }
      }
    }
  };

  // Focus the next question's input or the send button
  const focusNext = (currentIndex: number) => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < questions.length) {
      const nextQuestion = questions[nextIndex]!.question;
      const nextInput = inputRefs.current[nextQuestion];
      if (nextInput) {
        nextInput.focus();
      }
    } else if (sendButtonRef.current) {
      sendButtonRef.current.focus();
    }
  };

  if (!interactive) {
    return (
      <>
        {questions.map((question, index) => {
          return (
            <p key={`${msgId}-question-${index}`} className="">
              {question.question}
            </p>
          );
        })}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {questions.map((question, index) => {
        const isMultipleChoice = question.type === 'multi';
        const isSingleChoice = question.type === 'single';
        const isFreeForm = question.type === 'text';

        const questionText = question.question;
        const pendingAnswer = chatInputState.getAnswer(questionText);
        const historicalAnswer = historicalAnswers[questionText];
        const answer = pendingAnswer ?? historicalAnswer;
        const isDisabled = !!historicalAnswer && !pendingAnswer;
        const inputValue = inputValues[questionText];

        const otherText = 'Something else...';
        const otherTextHint = 'Please specify in the chat';

        const handleSubmit = () => {
          // Prevent double-submit from Enter key + blur
          if (isSubmittingRef.current) return;

          const value = (inputValue ?? '').trim();
          if (isFreeForm) {
            if (value) {
              chatInputState.answerQuestion({ question: questionText, answer: value });
            } else {
              chatInputState.removeAnswer(questionText);
            }
          } else if (value) {
            // For multi-choice "Other": save the answer and clear just the input value (not showOtherInput)
            chatInputState.toggleAnswer({ question: questionText, answer: value });
            setInputValues((prev) => ({ ...prev, [questionText]: '' }));
          }
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
            isSubmittingRef.current = true;
            setTimeout(() => {
              focusNext(index);
              isSubmittingRef.current = false;
            }, 0);
          } else if (e.key === 'Escape') {
            clearInput(questionText);
          }
        };

        function resizeTextarea() {
          if (inputRefs.current[questionText]) {
            const target = inputRefs.current[questionText];
            target.style.height = 'auto';
            target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
          }
        }

        const displayValue = isFreeForm
          ? inputValue !== undefined
            ? inputValue
            : typeof answer === 'string'
              ? answer
              : ''
          : (inputValue ?? '');

        return (
          <div key={`${msgId}-question-${index}`} className="@container flex flex-col gap-4">
            {/* Question text */}
            <div>
              <p className="m-0 font-medium">{questionText}</p>
              <div className="text-muted-foreground text-sm">
                {isMultipleChoice && 'Select one or more options.'}
                {isSingleChoice && 'Select one option.'}
              </div>
            </div>

            {/* Single-choice: Radio group */}
            {isSingleChoice && (
              <RadioGroup
                value={answer as string}
                onValueChange={(value: string) => {
                  chatInputState.answerQuestion({ question: questionText, answer: value });
                }}
                className={cn('grid grid-cols-1 @lg:grid-cols-2 gap-2', isDisabled && 'opacity-60 pointer-events-none')}
              >
                {question.choices?.map((choice) => (
                  <label
                    key={`${msgId}-choice-label-${index}-${choice}`}
                    className={cn(
                      'flex items-center gap-3 px-3 py-3 text-left rounded transition-colors border bg-background text-foreground hover:bg-background-secondary hover:text-foreground',
                      isSelected(answer, choice) ? ' border-accent' : null,
                    )}
                  >
                    <RadioGroupItem
                      key={`${msgId}-choice-radio-${index}-${choice}`}
                      value={choice}
                      checked={isSelected(answer, choice)}
                    />
                    <span>{choice}</span>
                  </label>
                ))}

                <label
                  key={`${msgId}-choice-label-other`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-3 text-left rounded transition-colors border bg-background text-foreground hover:bg-background-secondary hover:text-foreground',
                    isSelected(answer, otherText) ? 'border-accent' : null,
                  )}
                >
                  <RadioGroupItem
                    key={`${msgId}-choice-radio-${index}-other`}
                    value={otherText}
                    checked={isSelected(answer, otherText)}
                  />
                  <div className="flex flex-col">
                    <div>{otherText}</div>
                    {isSelected(answer, otherText) && (
                      <div className="text-xs text-muted-foreground">{otherTextHint}</div>
                    )}
                  </div>
                </label>
              </RadioGroup>
            )}

            {/* Multi-choice: Checkboxes */}
            {isMultipleChoice && (
              <div
                className={cn('grid grid-cols-1 @lg:grid-cols-2 gap-2', isDisabled && 'opacity-60 pointer-events-none')}
              >
                {question.choices?.map((choice) => {
                  const checked = isSelected(answer, choice);
                  return (
                    <label
                      key={`${msgId}-choice-label-${index}-${choice}`}
                      className={cn(
                        'flex items-center gap-3 px-3 py-3 text-left rounded transition-colors border bg-background text-foreground hover:bg-background-secondary hover:text-foreground',
                        checked ? ' border-accent' : null,
                      )}
                    >
                      <Checkbox
                        key={`${msgId}-choice-checkbox-${index}-${choice}`}
                        checked={checked}
                        onCheckedChange={() => chatInputState.toggleAnswer({ question: questionText, answer: choice })}
                        className={cn('flex-shrink-0')}
                      />
                      <span>{choice}</span>
                    </label>
                  );
                })}

                <label
                  key={`${msgId}-choice-label-other`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-3 text-left rounded transition-colors border bg-background text-foreground hover:bg-background-secondary hover:text-foreground',
                    isSelected(answer, 'Something else...') ? ' border-accent' : null,
                  )}
                >
                  <Checkbox
                    key={`${msgId}-choice-checkbox-${index}-other`}
                    checked={isSelected(answer, otherText)}
                    onCheckedChange={() => chatInputState.toggleAnswer({ question: questionText, answer: otherText })}
                    className={cn('flex-shrink-0')}
                  />
                  <div className="flex flex-col">
                    <div>{otherText}</div>
                    {isSelected(answer, otherText) && (
                      <div className="text-xs text-muted-foreground">{otherTextHint}</div>
                    )}
                  </div>
                </label>
              </div>
            )}

            {/* Free-form: Input field */}
            {isFreeForm && (
              <div className={cn('flex items-center gap-2', isDisabled && 'opacity-60 pointer-events-none')}>
                <Textarea
                  ref={(el) => {
                    inputRefs.current[questionText] = el;
                  }}
                  placeholder="Type your answer..."
                  value={displayValue}
                  disabled={isDisabled}
                  rows={1}
                  onInput={resizeTextarea}
                  onChange={(e) => updateInput(questionText, e.target.value)}
                  onBlur={handleSubmit}
                  onKeyDown={handleKeyDown}
                  className="text-base sm:text-base"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
